import { MapPin } from "@/components/icons";
import { Textarea } from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type { CustomerAddressFormValue } from "@/modules/customers/domain/address";
import { useAddressOptionsQuery } from "@/modules/customers/presentation/api/queries";
import { SuggestTextField } from "@/shared/ui/SuggestTextField";

type CustomerAddressFieldsProps = {
  value: CustomerAddressFormValue;
  onChange: (next: CustomerAddressFormValue) => void;
  idPrefix?: string;
  className?: string;
  /** แสดงที่อยู่ก้อนเดิมถ้ายังไม่มีฟิลด์แยก */
  legacyAddressHint?: string | null;
};

export function CustomerAddressFields({
  value,
  onChange,
  idPrefix,
  className,
  legacyAddressHint,
}: CustomerAddressFieldsProps) {
  const prefix = idPrefix ?? "addr";

  const provinceQ = value.province.trim();
  const districtQ = value.district.trim();
  const villageQ = value.village.trim();

  const provinces = useAddressOptionsQuery({
    level: "province",
    q: provinceQ || undefined,
  });
  const districts = useAddressOptionsQuery(
    {
      level: "district",
      province: provinceQ,
      q: districtQ || undefined,
    },
    Boolean(provinceQ),
  );
  const villages = useAddressOptionsQuery(
    {
      level: "village",
      province: provinceQ,
      district: districtQ,
      q: villageQ || undefined,
    },
    Boolean(provinceQ && districtQ),
  );

  const startedSplit = Boolean(
    value.province.trim() || value.district.trim() || value.village.trim(),
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <MapPin className="size-4 shrink-0" />
        <span>ທີ່ຢູ່ຈັດສົ່ງ</span>
      </div>

      {legacyAddressHint && !startedSplit ? (
        <div className="flex flex-col gap-1 rounded-lg bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
          <p className="font-medium text-foreground">
            ທີ່ຢູ່ເກົ່າຍັງບໍ່ໄດ້ແຍກ — ກະລຸນາເລືອກ/ພິມ ແຂວງ · ເມືອງ · ບ້ານ
          </p>
          <p className="whitespace-pre-line">{legacyAddressHint}</p>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <SuggestTextField
          id={`${prefix}-province`}
          label="ແຂວງ"
          value={value.province}
          placeholder="ເຊັ່ນ ວຽງຈັນ"
          options={provinces.data?.data ?? []}
          onChange={(province) =>
            onChange({
              ...value,
              province,
              district: "",
              village: "",
            })
          }
        />
        <SuggestTextField
          id={`${prefix}-district`}
          label="ເມືອງ"
          value={value.district}
          placeholder={provinceQ ? "ເລືອກ ຫຼື ພິມເມືອງ" : "ເລືອກແຂວງກ່ອນ"}
          disabled={!provinceQ}
          options={districts.data?.data ?? []}
          onChange={(district) =>
            onChange({
              ...value,
              district,
              village: "",
            })
          }
        />
      </div>

      <SuggestTextField
        id={`${prefix}-village`}
        label="ບ້ານ"
        value={value.village}
        placeholder={
          provinceQ && districtQ ? "ເລືອກ ຫຼື ພິມບ້ານ" : "ເລືອກເມືອງກ່ອນ"
        }
        disabled={!provinceQ || !districtQ}
        options={villages.data?.data ?? []}
        onChange={(village) => onChange({ ...value, village })}
      />

      <FieldLayout>
        <FieldLabel htmlFor={`${prefix}-line`}>ຈຸດສັງເກດ (ຖ້າມີ)</FieldLabel>
        <FieldContent>
          <Textarea
            id={`${prefix}-line`}
            value={value.addressLine}
            onChange={(e) =>
              onChange({ ...value, addressLine: e.target.value })
            }
            rows={2}
            placeholder="ເລກບ້ານ, ຕະຫຼາດ, ຈຸດສັງເກດ..."
          />
        </FieldContent>
      </FieldLayout>
    </div>
  );
}
